import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Vacancy } from '@mkp/vacancy/models';
import { CreditRedemptionUtils } from '@mkp/shared/util-product';
import { CreditRedemptionState, CreditRedemptionViewModel, ProductCode } from '@mkp/shared/models';
import { LabelComponent, LabelConfig } from '@mkp/shared/ui-library';
import { CLIENT_ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment/client';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';

const labelTexts: { [key: string]: string } = {
  [CreditRedemptionState.Active]: 'CREDIT_REDEMPTION.ONLINE',
  [CreditRedemptionState.Inactive]: 'CREDIT_REDEMPTION.OFFLINE',
  [CreditRedemptionState.Expired]: 'CREDIT_REDEMPTION.OFFLINE',
  [CreditRedemptionState.Stopped]: 'CREDIT_REDEMPTION.OFFLINE',
  [CreditRedemptionState.Pending]: 'CREDIT_REDEMPTION.SCHEDULED',
  [CreditRedemptionState.Draft]: 'CREDIT_REDEMPTION.DRAFT',
  Imported: 'CREDIT_REDEMPTION.IMPORTED', // TODO: remove when we no longer have imported job ads - CDP-1098
};

const labelConfigs: { [key: string]: LabelConfig } = {
  [CreditRedemptionState.Active]: { color: 'green' },
  [CreditRedemptionState.Inactive]: { color: 'grey' },
  [CreditRedemptionState.Expired]: { color: 'grey' },
  [CreditRedemptionState.Stopped]: { color: 'grey' },
  [CreditRedemptionState.Pending]: { color: 'purple' },
  [CreditRedemptionState.Draft]: { color: 'yellow' },
};

@Component({
  selector: 'mkp-credit-redeem-status',
  templateUrl: './credit-redeem-status.component.html',
  styleUrl: './credit-redeem-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, LabelComponent],
})
export class CreditRedemptionStatusComponent {
  readonly vacancy = input.required<Vacancy>();
  readonly creditRedeem = input<CreditRedemptionViewModel | undefined>();
  readonly showCreditMessage = input(true);
  readonly showLabelText = input(true);
  readonly isWrapperFlex = input(false);

  private readonly translateService = inject(TranslateService);
  private readonly createdByIdsForImportedJobAds = inject(CLIENT_ENVIRONMENT_PROVIDER)
    .createdByIdsForImportedJobAds;

  readonly creditMessage = computed(() =>
    getCreditMessage(this.creditRedeem(), this.vacancy(), this.translateService)
  );

  readonly labelConfig = computed(
    () => labelConfigs[this.creditRedeem()?._state ?? CreditRedemptionState.Draft]
  );

  readonly labelText = computed(() =>
    this.creditRedeem()
      ? (labelTexts[this.creditRedeem()?._state ?? ''] ?? labelTexts[CreditRedemptionState.Draft])
      : shouldLabelAsDraftOrImported(this.vacancy(), this.createdByIdsForImportedJobAds)
  );
}

export function getCreditMessage(
  creditRedeem: CreditRedemptionViewModel | undefined,
  vacancy: Vacancy,
  translateService: TranslateService
): string | null {
  if (!creditRedeem) {
    return translateService.instant('CREDIT_REDEMPTION.CREATED_ON', {
      day: vacancy.createdAt ? format(new Date(vacancy.createdAt), 'dd.MM.yyyy') : 'unknown',
    });
  }

  const isPppProduct = creditRedeem.information.productCode === ProductCode.PAYPERPERFORMANCE;

  if (isPppProduct) {
    return translateService.instant('CREDIT_REDEMPTION.STARTED_ON', {
      day: formatDate(creditRedeem.start),
    });
  }

  // every other redemption shows a range: time left while it runs, last-online once it stops
  const endDate = creditRedeem.stoppedAt ?? creditRedeem.end;
  return `${formatDate(creditRedeem.start)} - ${formatDate(endDate)}`;
}

// date-fns `format` throws RangeError on an Invalid Date, and the mapper parses whatever the API
// sends: `parseISO('')` yields one. Returning null degrades the same way the string-typed
// predecessor did rather than taking down the computed() this feeds.
const formatDate = (date: Date): string | null =>
  Number.isNaN(date.getTime()) ? null : format(date, 'dd.MM.yyyy');

// TODO: remove when we no longer have imported job ads - CDP-1098
const shouldLabelAsDraftOrImported = (vacancy: Vacancy, createdByIds: string[]): string =>
  CreditRedemptionUtils.isImported(vacancy, createdByIds)
    ? labelTexts['Imported']
    : labelTexts[CreditRedemptionState.Draft];
